import React, { useState, useEffect, useMemo } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import Select, { SingleValue } from 'react-select'
import { useAtomValue, useSetAtom, useAtom } from 'jotai'
import dt from 'dayjs'
import Decimal from 'decimal.js'
import * as lib from '../../lib'
import * as state from '../../states'
import {
	type IAccountBuyer,
	type IRevenueAgencyDropDown,
	ERoleAccess,
	IFetchAuthProfile
} from '../../types'
import {
	revenueAgencyOptionValue,
	revenueAgencyOptionLabel,
	revenueAgencyFormatOptionLabel
} from './helpers'
import {
	accountOptionLabel
} from '../offerSpend/helpers'
import { useSearchParams } from 'react-router'
import { IconDeleteBin, IconSettings } from '../ui'
import styled from 'styled-components'
import { RevenueSpendUiSettingsModal } from './RevenueSpendUiSettingsModal'
import {
	AddBoxesWrapper,
	AddBoxes,
	AddBox,
	RemoveRecordBox,
	RemoveBtn,
	AddBoxesScroll
} from '../revenueAgency'
import { reactSelectStyles } from '../filters'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../../types/lang'

type IRevenueSpendCreateModalProps = {
	show: boolean
	accounts: Array<IAccountBuyer> | null
	revenueAgencies: Array<IRevenueAgencyDropDown> | null
	onClose: () => void
	onSave: () => void
}

type IMultipleRecord = {
	accountId: number | string;
	amount: string;
	notes: string;
}


const SelectUsersBox = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;

    gap: 15px;
    padding-bottom: 20px;
`

const AddBoxesFixedWidth = styled(AddBoxes)`
    min-width: 760px;
`

const AccountIdBox = styled.div`
    width: 50%;
`

const LoginKtBox = styled.div`
    width: 50%;
`

const AmountBox = styled.div`
    width: 50%;
`

const NotesBox = styled.div`
    width: 50%;
`

export const RevenueSpendCreateModal = (
	props: IRevenueSpendCreateModalProps
) => {
	const { t } = useTranslation()
	const [searchParams] = useSearchParams()
	const isThemeDark = useAtomValue(state.themeIsDarkAtom)
	const setFetchIsLoading = useSetAtom(state.fetchIsLoadingAtom)
	const tokens = useAtomValue(state.tokensAtom)
	const [profile, setProfile] = useAtom(state.profileAtom)
	const $access = new lib.Access(profile ?? null)

	const {
		accounts: allAccounts,
		revenueAgencies: allRevenueAgencies,
		show,
		onClose,
		onSave
	} = props

	const today = useMemo(() => dt.utc(new Date()).startOf('day'), [])

	const [viewRevenueSpendUiSettings, setViewRevenueSpendUiSettings] = useState<boolean>(false)

	const [revenueAgencyId, setRevenueAgencyId] = useState<number>(0)
	const [at, setAt] = useState<Date>(dt.utc(today).toDate())

	const initMultipleRecord: IMultipleRecord = {
		accountId: 0,
		amount: '',
		notes: ''
	}

	const [multipleRecords, setMultipleRecords] = useState<IMultipleRecord[]>([])
	const revenueSpendBuyersSaved = useMemo(
		() => profile?.setup?.ui?.revenueSpendBuyers?.[revenueAgencyId] ?? [],
		[profile, revenueAgencyId]
	)

	const currentRevenueAgency = allRevenueAgencies?.find(_ => _.id === revenueAgencyId) ?? null
	const isSelectUsersBtnActive = !!currentRevenueAgency
	const multipleRecordsOnlyValid = multipleRecords.filter(
		item => revenueAgencyId && at && item.accountId && !!item.amount && +item.amount >= 0
	)
	//TODO: check validation conditions
	const isCreateBtnActive = !!currentRevenueAgency && multipleRecordsOnlyValid.length > 0


	useEffect(() => {
		//console.log('RevenueSpendCreateModal --- useEffect: ', { revenueAgencyId, revenueSpendBuyersSaved, profile })
		setMultipleRecords(() => {
			if (!currentRevenueAgency) return []

			const allAccountsIds = allAccounts?.map(item => item.id) ?? []
			const revenueSpendBuyersSavedFiltered = revenueSpendBuyersSaved.filter(
				item => allAccountsIds?.includes(item)
			)
			const targetSpendBuyersList =
				revenueSpendBuyersSavedFiltered.length > 0 ?
					revenueSpendBuyersSavedFiltered :
					allAccountsIds

			return targetSpendBuyersList.map(accountId => (
				{
					...initMultipleRecord,
					accountId
				}
			))
		})
	}, [revenueSpendBuyersSaved, currentRevenueAgency])

	if (!show) {
		return null
	}

	const onCreate = async () => {
		const uri: string = `revenue/spend`
		const method: string = 'POST'
		const params: lib.FetchOptions['params'] = {}
		const headers = lib.bearer(tokens?.access)
		// multi-create request
		try {
			const body: string = JSON.stringify({
				items: multipleRecordsOnlyValid
					.map(item => (
						{
							// fixed params for all records
							agencyId: revenueAgencyId,
							at: dt.utc(at).format('YYYY-MM-DD'),
							// dynamic params for each record
							accountId: item.accountId,
							amount: !!item.amount ? new Decimal(item.amount).toNumber() : 0,
							notes: item.notes
						}
					))
			})
			const options: lib.FetchOptions = { body, method, params, headers }
			setFetchIsLoading(state.turnOnLoader)
			await lib.fetching<void>(uri, options)
			onSave()
		} catch (error: unknown) {
			// TODO: pretty notification alert
			console.error(error)
		} finally {
			setFetchIsLoading(state.turnOffLoader)
		}
	}

	// reset all ui settings - for dev aims now
	const onResetSetupUiSettings = async (event: React.MouseEvent) => {
		const uri = 'auth/profile/ui'
		const method: string = 'PUT'
		const params: lib.FetchOptions['params'] = {}
		const headers = lib.bearer(tokens?.access)
		try {
			const body: string = JSON.stringify({
				ui: {
					...profile?.setup?.ui,
					revenueSpendBuyers: {}
				}
			})
			const options: lib.FetchOptions = { body, method, params, headers }
			setFetchIsLoading(state.turnOnLoader)
			const updatedProfile = await lib.fetching<IFetchAuthProfile>(uri, options)
			updatedProfile && setProfile(updatedProfile.profile)
		} catch (error: unknown) {
			// TODO: pretty notification alert
			console.error(error)
		} finally {
			setFetchIsLoading(state.turnOffLoader)
		}
	}

	const onSelectUsersBtn = (event: React.MouseEvent) => {
		event.preventDefault()
		setViewRevenueSpendUiSettings(true)
	}
	const onRemoveRecord = (event: React.MouseEvent, index: number) => {
		event.preventDefault()

		setMultipleRecords((prev) => {
			const newArr = prev.filter((item, i) => i !== index)
			return newArr
		})
	}

	const renderSelectUsersBox = () => {
		return (
			<SelectUsersBox>
				<button
					type="button"
					className="btn btn-primary d-flex align-items-center"
					disabled={!isSelectUsersBtnActive}
					onClick={(e) => isSelectUsersBtnActive && onSelectUsersBtn(e)}
				>
					<IconSettings/>
					<span className="d-none d-lg-block">{`${t(ALL_LANG_KEY.revenue_spend_crud_modal.btn_select_users)}`}</span>
				</button>
				{/*TODO: the condition && logic && trans if necessary*/}
				{
					!!profile?.setup?.ui?.revenueSpendBuyers && false ? (
						(
							<button
								type="button"
								className="btn btn-primary btn-danger d-flex align-items-center"
								onClick={onResetSetupUiSettings}
							>
								<IconDeleteBin size={16}/>
								<span className="d-none d-lg-block">Reset All Setup Ui Settings</span>
							</button>
						)
					) : null
				}
			</SelectUsersBox>
		)
	}

	const renderMultipleRecords = () => {
		return multipleRecords.length > 0 ? (
			<AddBoxesScroll className={'scroll'}>
				{/*TODO: check styles for scrollable content*/}
				<AddBoxesFixedWidth>
					{multipleRecords.map((item, index) => {
						const isRemoveBtnActive = multipleRecords.length > 1
						const currentAccount = allAccounts?.find(_ => _.id === item.accountId) ?? null
						const currentAccountLabel = currentAccount ? accountOptionLabel(currentAccount, t) : ''
						const currentAccountLoginKt = currentAccount?.extra?.keitaroLogin ?? ''
						//console.log('--- render multipleRecords ---: ', { item, currentAccount, currentAccountLabel })
						return (
							<AddBox key={`add-box-${index}`}>
								<AccountIdBox>
									<Form.Label className="text-dark" htmlFor={`input-account-${index}`}>{`${t(ALL_LANG_KEY.revenue_spend_crud_modal.field_account_label)}`}</Form.Label>
									<Form.Control
										disabled={true}
										id={`input-account-${index}`}
										placeholder={`${t(ALL_LANG_KEY.revenue_spend_crud_modal.field_account_placeholder)}`}
										type="text"
										title={currentAccountLabel}
										value={currentAccountLabel}
										onChange={(e) => {
											/*const value = e.target.value
											setMultipleRecords((prev) => {
												const newArr = [...prev]
												newArr[index].accountId = value

												return newArr
											})*/
										}}
									/>
								</AccountIdBox>
								<LoginKtBox>
									<Form.Label className="text-dark" htmlFor={`input-account-login-kt-${index}`}>{`${t(ALL_LANG_KEY.revenue_spend_crud_modal.field_kt_group_label)}`}</Form.Label>
									<Form.Control
										disabled={true}
										id={`input-account-login-kt-${index}`}
										placeholder=""
										type="text"
										title={currentAccountLoginKt}
										value={currentAccountLoginKt}
										onChange={(e) => {
											//const value = e.target.value
										}}
									/>
								</LoginKtBox>
								<AmountBox>
									<Form.Label className="text-dark" htmlFor={`input-amount-${index}`}>{`${t(ALL_LANG_KEY.revenue_spend_crud_modal.field_amount_label)}`}</Form.Label>
									<Form.Control
										id={`input-amount-${index}`}
										placeholder="0"
										type="number"
										value={item.amount ?? ''}
										onChange={(e) => {
											const value = e.target.value

											setMultipleRecords((prev) => {
												const newArr = [...prev]
												newArr[index].amount = value

												return newArr
											})
										}}
									/>
								</AmountBox>
								<NotesBox>
									<Form.Label className="text-dark" htmlFor={`input-notes-${index}`}>{`${t(ALL_LANG_KEY.revenue_spend_crud_modal.field_notes_label)}`}</Form.Label>
									<Form.Control
										id={`input-notes-${index}`}
										placeholder=""
										type="text"
										value={item.notes ?? ''}
										onChange={(e) => {
											const value = e.target.value

											setMultipleRecords((prev) => {
												const newArr = [...prev]
												newArr[index].notes = value

												return newArr
											})
										}}
									/>
								</NotesBox>

								<RemoveRecordBox>
									<RemoveBtn data-disabled={!isRemoveBtnActive}
									           onClick={(e) => isRemoveBtnActive && onRemoveRecord(e, index)}
									>
										<IconDeleteBin size={16}/>
									</RemoveBtn>
								</RemoveRecordBox>
							</AddBox>
						)
					})}
				</AddBoxesFixedWidth>
			</AddBoxesScroll>
		) : null
	}

	const modalTitle = t(ALL_LANG_KEY.revenue_spend_crud_modal.title_create)

	return <>
		<Modal show={show} onHide={onClose} size="xl" className={'scroll'}>
			<Modal.Header closeButton>
				<Modal.Title>{modalTitle}</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				<Form>
					<Form.Group>

						<Form.Label className="text-dark" htmlFor="select-revenue-agency">{`${t(ALL_LANG_KEY.revenue_spend_crud_modal.field_agency_label)}`}</Form.Label>
						<Select
							inputId="select-revenue-agency"
							className="react-select-container"
							options={allRevenueAgencies ?? []}
							value={currentRevenueAgency}
							placeholder={`${t(ALL_LANG_KEY.general.react_select_placeholder)}`}
							noOptionsMessage={() => `${t(ALL_LANG_KEY.general.react_select_no_options)}`}
							getOptionValue={revenueAgencyOptionValue}
							getOptionLabel={revenueAgencyOptionLabel}
							formatOptionLabel={revenueAgencyFormatOptionLabel}
							styles={reactSelectStyles<IRevenueAgencyDropDown, false>(isThemeDark)}
							onChange={(_: SingleValue<IRevenueAgencyDropDown>) => setRevenueAgencyId(_?.id ?? 0)}
							isDisabled={false}
							isSearchable={true}
						/>
						<br/>

						<Form.Label className="text-dark" htmlFor="input-at">{`${t(ALL_LANG_KEY.revenue_spend_crud_modal.field_date_label)}`}</Form.Label>
						<Form.Control
							id="input-at"
							placeholder={`${t(ALL_LANG_KEY.revenue_spend_crud_modal.field_date_placeholder)}`}
							type="date"
							value={dt.utc(at).format('YYYY-MM-DD')}
							disabled={false}
							onChange={
								(e) => {
									const v = dt.utc(e.target.value)
									v.isValid() && setAt(v.startOf('day').toDate())
								}
							}
						/>
						<br/>

						<AddBoxesWrapper>
							{renderSelectUsersBox()}

							{renderMultipleRecords()}
						</AddBoxesWrapper>

					</Form.Group>
				</Form>
			</Modal.Body>

			<Modal.Footer>
				<Button variant="primary" onClick={onCreate} disabled={!isCreateBtnActive}>{`${t(ALL_LANG_KEY.revenue_spend_crud_modal.btn_create)}`}</Button>
				<Button variant="secondary" onClick={onClose}>{`${t(ALL_LANG_KEY.revenue_spend_crud_modal.btn_close)}`}</Button>
			</Modal.Footer>
		</Modal>

		{
			!!viewRevenueSpendUiSettings && isSelectUsersBtnActive && !!$access.$(ERoleAccess.RevenueSpendList) && (
				<RevenueSpendUiSettingsModal
					revenueAgency={currentRevenueAgency}
					show={!!viewRevenueSpendUiSettings}
					take={'all'}
					onClose={() => {
						setViewRevenueSpendUiSettings(false)
					}}
					onSave={() => {
						setViewRevenueSpendUiSettings(false)
					}}
				/>
			)
		}
	</>
}
